import {
  selectOrganizationId,
  useGetIntegrationDataQuery,
  useGetIntegrationsConfigQuery,
  useGetIntegrationsQuery,
} from '@carboncopyco/data';
import type { Opportunity, OpportunityFilter } from '@carboncopyco/types';
import { flip, offset, useFloating } from '@floating-ui/react';
import { Combobox } from '@headlessui/react';
import { useField, useFormikContext } from 'formik';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import { ValidationErrorMessages } from '../../consts';
import { OpportunityInput } from './OpportunityInput';
import { OpportunityOptions } from './OpportunityOptions';

type OpportunitySelector = {
  integrationLogo: React.ReactElement;
  clientFieldName?: string;
  isIntegrationConnected: boolean;
};

export const OpportunitySelector = React.forwardRef(
  OpportunitiesDropdownFieldInner
);

function OpportunitiesDropdownFieldInner<
  S extends { opportunity: Opportunity },
>(
  {
    integrationLogo,
    clientFieldName = 'client',
    isIntegrationConnected,
  }: OpportunitySelector,
  ref: React.ForwardedRef<HTMLInputElement>
) {
  const [opportunityFilter, setOpportunityFilter] = useState<OpportunityFilter>(
    'opportunitiesOwnedByCurrentUser'
  );
  const [query, setQuery] = useState('');
  const [freeformText, setFreeformText] = useState(query);

  useEffect(() => {
    setFreeformText(query);
  }, [query]);

  const [debouncedQuery] = useDebounce(query, 350);
  const organizationIdFromStore = useSelector(selectOrganizationId);
  const { organizationId: organizationIdFromParams } = useParams();
  const organizationId = organizationIdFromParams || organizationIdFromStore;
  const { data: integrations } = useGetIntegrationsQuery({
    organizationId,
  });
  const {
    data: { crm },
  } = useGetIntegrationsConfigQuery({ organizationId });
  const integrationId = integrations?.find(
    (integration) => integration.integrationType === crm
  )?.id;
  const { data: integrationData, isFetching } = useGetIntegrationDataQuery(
    {
      organizationId,
      integrationId,
      filter: opportunityFilter,
      search:
        opportunityFilter === 'opportunitiesExceptCurrentUser'
          ? debouncedQuery
          : undefined,
    },
    {
      skip:
        !integrationId ||
        (opportunityFilter === 'opportunitiesExceptCurrentUser' &&
          debouncedQuery === ''),
    }
  );
  const opportunities = integrationData?.[opportunityFilter] || [];
  const { values, setValues } = useFormikContext<S>();
  const [opportunityField] = useField({
    name: 'opportunity',
    validate: (value: Opportunity | null) => {
      if (!value?.name) {
        return ValidationErrorMessages.required;
      }
      return;
    },
  });
  const { refs, floatingStyles } = useFloating({
    middleware: [flip(), offset(8)],
  });

  return (
    <Combobox
      value={opportunityField.value}
      onChange={(value) => {
        const textValue = value?.name || '';
        setValues({
          ...values,
          opportunity: value,
          [clientFieldName]: textValue,
        });
        setFreeformText(textValue);
      }}
    >
      {({ open }) => (
        <div className="w-full relative">
          <div ref={refs.setReference}>
            <OpportunityInput
              ref={ref}
              handleChange={(event) => setQuery(event.target.value)}
              logo={
                opportunities.find(
                  (opp) => opp.id === opportunityField.value?.id
                ) &&
                !open &&
                integrationLogo
              }
            />
          </div>
          <Combobox.Options>
            <div
              ref={refs.setFloating}
              className="w-full z-10"
              style={floatingStyles}
            >
              <OpportunityOptions
                query={query}
                freeformText={freeformText}
                opportunities={opportunities}
                isLoading={isFetching}
                isIntegrationConnected={isIntegrationConnected}
                opportunityFilter={opportunityFilter}
                setOpportunityFilter={setOpportunityFilter}
              />
            </div>
          </Combobox.Options>
        </div>
      )}
    </Combobox>
  );
}
